import React from "react";
import { formatCurrency } from "../../../../utils/formatters";
import { motion, AnimatePresence } from "framer-motion";

const AssetsForm = ({ data, onChange }) => {
  const ownerOptions = [
    { value: "self", label: "Self" },
    { value: "spouse", label: "Spouse" },
    { value: "joint", label: "Joint" },
  ];

  const assetTypeOptions = [
    { value: "retirement_account", label: "Retirement Account" },
    { value: "bank_account", label: "Bank Account" },
    { value: "investment_account", label: "Investment Account" },
    { value: "real_estate", label: "Real Estate" },
    { value: "other", label: "Other" },
  ];

  const taxQualificationOptions = [
    { value: "tax_deferred", label: "Traditional or Tax-Deferred" },
    { value: "roth", label: "Roth" },
    { value: "non_qualified", label: "Non-Qualified" },
    { value: "other", label: "Other" },
  ];

  const accountTypeOptions = [
    { value: "401k", label: "401(k)" },
    { value: "ira", label: "IRA" },
    { value: "joint", label: "Joint" },
    { value: "checking", label: "Checking" },
    { value: "savings", label: "Savings" },
    { value: "other", label: "Other" },
  ];

  const handleAddAsset = () => {
    onChange([
      ...data,
      {
        id: `new-${Date.now()}`,
        financialInstitution: "",
        ownerType: "",
        assetType: "",
        taxQualification: "",
        accountType: "",
        balance: "",
        otherOwnerType: "",
        otherAssetType: "",
        otherAccountType: "",
        showOtherOwner: false,
        showOtherAsset: false,
        showOtherAccount: false,
      },
    ]);
  };

  const handleAssetChange = (index, field, value) => {
    const updatedAssets = [...data];
    updatedAssets[index] = {
      ...updatedAssets[index],
      [field]: value,
      ...(field === "ownerType" && value === "8"
        ? { showOtherOwner: true }
        : {}),
      ...(field === "assetType" && value === "8"
        ? { showOtherAsset: true }
        : {}),
      ...(field === "accountType" && value === "8"
        ? { showOtherAccount: true }
        : {}),
    };
    onChange(updatedAssets);
  };

  const handleDeleteAsset = (index) => {
    onChange(data.filter((_, i) => i !== index));
  };

  const totalAssets = data.reduce(
    (sum, asset) =>
      sum + (parseFloat(asset.balance?.toString().replace(/[$,]/g, "")) || 0),
    0,
  );

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Assets</h3>
        <button
          onClick={handleAddAsset}
          type="button"
          className="inline-flex items-center px-4 py-2 rounded-full border border-transparent shadow-sm text-sm font-medium text-white bg-[#131d80] hover:bg-[#0f1660]"
        >
          <svg
            className="w-5 h-5 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add Asset
        </button>
      </div>

      <div className="space-y-3">
        <AnimatePresence>
          {data.map((asset, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-surface-default dark:bg-surface-default rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:border-gray-300 transition-all duration-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="relative">
                  <input
                    type="text"
                    value={asset.financialInstitution}
                    onChange={(e) =>
                      handleAssetChange(
                        index,
                        "financialInstitution",
                        e.target.value,
                      )
                    }
                    className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder=" "
                  />
                  <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                    Financial Institution
                  </label>
                </div>

                <div className="relative">
                  <select
                    value={asset.ownerType}
                    onChange={(e) =>
                      handleAssetChange(index, "ownerType", e.target.value)
                    }
                    className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Owner</option>
                    {ownerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                    Owner
                  </label>
                </div>

                <div className="relative">
                  <select
                    value={asset.assetType}
                    onChange={(e) =>
                      handleAssetChange(index, "assetType", e.target.value)
                    }
                    className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    {assetTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                    Asset Type
                  </label>
                </div>

                <div className="relative">
                  <select
                    value={asset.taxQualification}
                    onChange={(e) =>
                      handleAssetChange(
                        index,
                        "taxQualification",
                        e.target.value,
                      )
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  >
                    <option value="">Select Type</option>
                    {taxQualificationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                    Tax Qualification
                  </label>
                </div>

                <div className="relative">
                  <select
                    value={asset.accountType}
                    onChange={(e) =>
                      handleAssetChange(index, "accountType", e.target.value)
                    }
                    className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Account Type</option>
                    {accountTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                    Account Type
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={asset.balance}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d.]/g, "");
                      handleAssetChange(index, "balance", value);
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value)) {
                        handleAssetChange(
                          index,
                          "balance",
                          formatCurrency(value, false).replace(/[$,]/g, ""),
                        );
                      }
                    }}
                    className="peer w-full h-12 border border-gray-300 rounded-lg px-8 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                    placeholder=" "
                  />
                  <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                    Balance
                  </label>
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-500">
                    $
                  </span>
                </div>

                <div className="flex items-center justify-end">
                  <button
                    onClick={() => handleDeleteAsset(index)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200 rounded-full hover:bg-red-50"
                    title="Remove asset"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="mt-4 p-4 bg-gray-50 rounded">
        <div className="text-lg font-medium">
          Total Assets: {formatCurrency(totalAssets)}
        </div>
      </div>
    </div>
  );
};

export default AssetsForm;
